<template>
  <div class="rightSectionBg">
    <div class="columns flex flex-col w-full container">
      <div class="flex flex-col w-full bg-white">
        <div class="pt-[3.714rem] pb-[1.357rem] mb-[1.5rem] px-[1.429rem] lg:px-0 border-b border-gray-300" v-if="!isSearching">
          <Heading :level="2" color="text-ash-200"> {{ headingText }} </Heading>
        </div>

        <div
          v-if="isSearching"
          class="border-r-0 md:border-r md:border-gray-300 lg:col-span-3
                 flex items-center justify-center py-20"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span class="sr-only">Loading search results…</span>
        </div>

        <div class="px-[1.429rem] lg:px-0" v-if="!isSearching && paginatedFaqs.length">
          <div
            v-for="(faq, index) in paginatedFaqs"
            :key="faq.link || `${index}-${faq.question}`"
            class="flex flex-row w-full pt-[2.429rem] pb-[2.643rem] border-b border-gray-300 last:border-0"
          >
            <div class="pt-[1.142857rem] pl-0 mr-[0.75rem] text-gold-200 text-[1.286rem]">
              <Icon class="text-2xl" icon="circle-question" />
            </div>
            <div class="text-[1rem] p-[0.75rem] max-w-[80%]">
              <NuxtLink
                :to="faq.link"
                class="text-gray-900 group hover:text-gold-100 active:text-gold-100"
                :aria-label="faq.question"
                :title="faq.question"
                :id="`ques-${index}`"
                external
              >
                <Heading :level="3" class="hover:text-gold-100" color="text-ash-500"> {{ faq.question }} </Heading>
              </NuxtLink>
              <div
                class="text-gray-700 mb-[1rem] pt-[0.2857rem] line-clamp-2"
                v-html="sanitizeHtml(faq.answer)"
              ></div>
            </div>
          </div>
        </div>

        <div class="no-results container mt-10" v-if="!isSearching && isEmpty">
          <p>
            Browse our
            <NuxtLink class="underline" :to="URL_CONFIG.FAQS" aria-label="main support page" title="main support page" id="main-support-page" external>main support page</NuxtLink>.
          </p>
        </div>

        <Pagination
          v-if="showPagination && !isSearching"
          :total-pages="totalPages"
          :initial-page="currentPage"
          :limit="pageSize"
          :total-count="filteredFaqs.length"
          show-page-numbers
          id='faq-list-pagination'
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, nextTick, computed, ref, watchEffect } from "vue"
import { useRoute } from "vue-router"
import { useFaqList } from "~/composables/useFaqs"
import { CATEGORY_DETAILS } from '~~/shared/utils/contentTypes'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import Icon from "../../atom/icon.vue"
import Pagination from "../../organism/pagination.vue"
import Heading from "../../atom/heading.vue"
import { sanitizeHtml } from "~~/shared/utils/sanitize"
import { normalizePath } from "~~/shared/utils/helper"

const titleFromSlug = (slug: string) => {
  return slug
    .toLowerCase()
    .replace(/[-_/]+/g, ' ')
    .replace(/^\s*\w/, (c) => c.toUpperCase())
    .replace(/\b(?!and\b)\w/g, (c) => c.toUpperCase());
}

const route = useRoute();
const slug =
  route?.path
    ?.split('/')
    .findLast(Boolean) ?? '';
const formattedTitle = titleFromSlug(slug === 'search' ? 'search results' : slug)
const question = `FAQs ${formattedTitle}`;
const pageTitle = computed(() => `${question} | Thorne`)

useHead({ title: pageTitle })

type FaqItem = {
  question: string
  answer: string
  link: string
}

const { data, pending, error } = useFaqList?.() ?? {}

const faqs = computed<FaqItem[]>(() => {
  const raw: any = data?.value
  const list: any[] = Array.isArray(raw) ? raw : (raw?.results ?? [])
  if (!Array.isArray(list) || list.length === 0) return []

  const looksNormalized =
    list.length > 0 &&
    typeof list[0] === 'object' && list[0] !== null &&
    'question' in list[0] && 'answer' in list[0]

  if (looksNormalized) {
    return list.map((item: any) => {
      if ('link' in item && item.link) return item as FaqItem
      const data = item?.data ?? item
      const link = data?.faqUrlKey ? `${URL_CONFIG?.FAQ}/${data.faqUrlKey}` : (item.link ?? '')
      return {
        question: item.question ?? data?.faqQuestion ?? '',
        answer: item.answer ?? data?.faqAnswer ?? '',
        link
      }
    })
  }

  return list.map((item: any) => {
    const data = item?.data ?? {}
    return {
      question: data.faqQuestion ?? '',
      answer: data.faqAnswer ?? '',
      link: data.faqUrlKey ? `${URL_CONFIG?.FAQ}/${data.faqUrlKey}` : ''
    }
  }).filter((f: FaqItem) => f.question && f.answer)
})

const displayCount = computed(() => filteredFaqs.value.length)
const queryParam = computed(() => String(route?.query?.q ?? '').trim())

const categoryName = computed(() => {
  const slug = route?.params?.slug?.[0]

  if (slug === 'search' && queryParam.value) {
    const query = queryParam.value.toLowerCase()
    const match = CATEGORY_DETAILS.find(item =>
      item.categoryName.toLowerCase().includes(query)
    )
    if (match) return match.categoryName.replaceAll('&', 'and')
    return `Search results`
  }

  const path = normalizePath(route.path)
  const match = CATEGORY_DETAILS.find(item => normalizePath(item.url) === path)
  return match?.categoryName?.replaceAll('&', 'and') ?? ''
})

const searchDisplayLabel = computed(() => {
  if (isSearch.value && hasQuery.value) {
    return queryParam.value
  }
  return categoryName.value
})

const hasLoaded = ref(false)
watchEffect(() => {
  if (!pending?.value) hasLoaded.value = true
})
const isLoading = computed(() => !!pending?.value)
const hasError = computed(() => !!error?.value)

const isSearch = computed(() => route?.params?.slug?.[0] === 'search')
const hasQuery = computed(() => Boolean(queryParam.value))

const isSearching = computed(() =>
  isSearch.value && hasQuery.value && pending?.value && !hasLoaded?.value
)

const filteredFaqs = computed(() => {
  const all = faqs.value
  if (!queryParam.value) return all

  // Decode safely; replace '+' with space
  let decoded = queryParam.value
  try {
    decoded = decodeURIComponent(decoded)
  } catch {
    // keep raw if decode fails
  }
  decoded = decoded.replaceAll('+', ' ').trim().toLowerCase()

  // Split into tokens and strip trailing punctuation that often appears in queries
  const PUNCT = '?.!,;:';

  const tokens = decoded
    .split(/\s+/)
    .map(t => {
      let end = t.length;
      while (end > 0 && PUNCT.includes(t[end - 1]!)) {
        end--;
      }
      return t.slice(0, end).trim();
    })
    .filter(Boolean);

  if (!tokens.length) return all

  return all.filter(f => {
    const question = (f.question || '').toLowerCase()
    const answer = (f.answer || '').toLowerCase()
    // ANY-token match (user-friendly)
    return tokens.some(t => question.includes(t) || answer.includes(t))
  })
})

const headingText = computed(() => {
  if (
    isSearch.value &&
    hasQuery.value &&
    hasLoaded.value &&
    displayCount.value === 0
  ) {
    return `No results for '${queryParam.value}'`
  }

  if (
    isSearch.value &&
    hasQuery.value &&
    displayCount.value > 0
  ) {
    return `${displayCount.value} articles for '${searchDisplayLabel.value}'`
  }

  return `${displayCount.value} articles for ${categoryName.value}`
})

const pageSize = 10
const currentPage = computed(() => Number(route?.query?.page) || 1)
const totalPages = computed(() => Math.ceil(filteredFaqs.value.length / pageSize))

const paginatedFaqs = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return filteredFaqs.value.slice(start, start + pageSize)
})

const isEmpty = computed(() =>
  !isLoading.value &&
  hasLoaded.value &&
  !hasError.value &&
  filteredFaqs.value.length === 0
)

const showPagination = computed(() =>
  !isLoading.value &&
  hasLoaded.value &&
  totalPages.value > 1
)

watch(currentPage, async () => {
  await nextTick()

  const mainSection = document.querySelector(".main")
  if (mainSection) {
    mainSection.scrollIntoView({
      behavior: "smooth",
      block: "start"
    })
  } else {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  }
})
</script>
